import { FC, useCallback, useState } from 'react';
import { CircularProgress } from '@mui/material';
import { Location } from 'types/location';
import { MigrationStep, MigrationStatusResponse, OPTIONAL_STEPS, STEP_ORDER, STEP_LABELS, STEP_DESCRIPTIONS } from 'types/migration';
import { useMigration } from 'hooks/use-migration';
import { useAuth } from 'contexts';
import { Role } from 'types';
import useLocationApi from 'hooks/use-location-api';
import { TextField } from 'components/inputs/text-field';
import StatusChip from './components/status-chip';
import ErrorList from './components/error-list';
import TimeZoneSelect from '../locations/time-zone';

const MigrationPage: FC = () => {
  const h = useMigration();
  const { userProfile } = useAuth();
  const isAdmin = userProfile.role === Role.ADMIN;

  const stepperItem = (
    step: MigrationStep, idx: number, status: any, isActive: boolean, canClick: boolean
  ) => (
    <div
      key={step}
      onClick={() => canClick && h.navigateToStep(step)}
      style={{
        display: 'flex', gap: 12, padding: '12px 0', alignItems: 'flex-start',
        cursor: canClick ? 'pointer' : 'default', opacity: canClick ? 1 : 0.4,
        borderLeft: isActive ? '3px solid #004A98' : '3px solid transparent',
        paddingLeft: isActive ? 9 : 12, transition: 'all 0.2s'
      }}
    >
      <span style={{
        width: 28, height: 28, borderRadius: '50%',
        backgroundColor: status === 'COMPLETED' ? '#2e7d32' : status === 'FAILED' ? '#d32f2f' : status === 'PARTIALLY_COMPLETED' ? '#f57f17' : isActive ? '#004A98' : '#e0e0e0',
        color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 13, fontWeight: 700, flexShrink: 0
      }}>
        {status === 'COMPLETED' ? '\u2713' : status === 'FAILED' || status === 'PARTIALLY_COMPLETED' ? '!' : idx + 1}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: isActive ? 700 : 500, fontSize: 14, color: isActive ? '#14284D' : '#616161' }}>
          {STEP_LABELS[step]}
        </div>
        <div style={{ fontSize: 11, color: '#9e9e9e', marginTop: 2 }}>
          <StatusChip status={status} />
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', height: '100%', gap: 24, padding: 24, backgroundColor: '#f7f7f7' }}>
      {/* Left: Stepper */}
      <div style={{ width: 260, flexShrink: 0 }}>
        <div style={{ marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#14284D' }}>Migration Wizard</h2>
          {h.selectedLocation && (
            <p style={{ margin: '4px 0 0', fontSize: 12, color: '#757575' }}>
              Location: {h.selectedLocation.name} ({h.selectedLocation.code})
              <button onClick={h.handleChangeLocation} style={{ background: 'none', border: 'none', color: '#004A98', cursor: 'pointer', fontSize: 11, marginLeft: 8, textDecoration: 'underline', padding: 0 }}>
                Change
              </button>
            </p>
          )}
          {h.migrationStatus && h.migrationStatus.overallStatus !== 'NOT_STARTED' && (
            <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ flex: 1, height: 6, backgroundColor: '#e0e0e0', borderRadius: 3, overflow: 'hidden' }}>
                <div style={{ width: `${h.overallProgress}%`, height: '100%', backgroundColor: '#004A98', borderRadius: 3, transition: 'width 0.3s' }} />
              </div>
              <span style={{ fontSize: 11, color: '#757575', fontWeight: 600 }}>{h.overallProgress}%</span>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {!h.selectedLocation ? (
            <div style={{ display: 'flex', gap: 12, padding: '12px 0', alignItems: 'flex-start', opacity: 0.5 }}>
              <span style={{ width: 28, height: 28, borderRadius: '50%', backgroundColor: '#004A98', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0 }}>1</span>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14, color: '#004A98' }}>Location Setup</div>
                <div style={{ fontSize: 12, color: '#9e9e9e' }}>Select or create a location</div>
              </div>
            </div>
          ) : (
            STEP_ORDER.map((step, idx) => {
              const stepStatus = h.migrationStatus?.steps?.[idx]?.status || 'PENDING' as const;
              return stepperItem(step, idx, stepStatus, step === h.activeStep, idx <= h.currentStepIndex + 1);
            })
          )}
        </div>

        {h.selectedLocation && isAdmin && h.migrationStatus?.canRestart && (
          <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid #e0e0e0' }}>
            {!h.showRestartConfirm ? (
              <button className="btn-danger" onClick={() => h.setShowRestartConfirm(true)} style={{ width: '100%' }} disabled={h.restarting}>
                {h.restarting ? 'Restarting...' : 'Restart Migration'}
              </button>
            ) : (
              <div style={{ backgroundColor: '#fff3e0', padding: 12, borderRadius: 8, border: '1px solid #ffe0b2' }}>
                <p style={{ margin: '0 0 8px', fontSize: 13, color: '#e65100' }}>
                  This permanently removes the records created by migration run #
                  {h.migrationStatus?.migrationRunId} for {h.selectedLocation.name} ({h.selectedLocation.code})
                  and resets the wizard. This cannot be undone.
                </p>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn-outline" onClick={() => h.setShowRestartConfirm(false)} style={{ flex: 1 }}>Cancel</button>
                  <button className="btn-danger" onClick={h.handleRestart} disabled={h.restarting} style={{ flex: 1 }}>
                    {h.restarting ? 'Restarting...' : 'Confirm Restart'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {h.selectedLocation && isAdmin && h.isStepInProgress && (
          <div style={{ marginTop: 12 }}>
            {!h.showCancelConfirm ? (
              <button className="btn-outline" onClick={() => h.setShowCancelConfirm(true)} disabled={h.cancelling} style={{ width: '100%', color: '#d32f2f', borderColor: '#d32f2f' }}>
                {h.cancelling ? 'Cancelling...' : 'Cancel Import'}
              </button>
            ) : (
              <div style={{ backgroundColor: '#fff3e0', padding: 12, borderRadius: 8, border: '1px solid #ffe0b2' }}>
                <p style={{ margin: '0 0 8px', fontSize: 13, color: '#e65100' }}>
                  This will stop the current import and remove all imported data for this location. This cannot be undone.
                </p>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn-outline" onClick={() => h.setShowCancelConfirm(false)} disabled={h.cancelling} style={{ flex: 1 }}>Keep Importing</button>
                  <button className="btn-danger" onClick={h.handleCancel} disabled={h.cancelling} style={{ flex: 1 }}>
                    {h.cancelling ? 'Cancelling...' : 'Confirm Cancel'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Right: Content */}
      <div style={{ flex: 1, minWidth: 0, position: 'relative' }}>
        {h.restarting && (
          <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(255,255,255,0.9)', borderRadius: 12, zIndex: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
            <CircularProgress size={56} sx={{ color: '#004A98' }} />
            <div style={{ textAlign: 'center' }}>
              <h3 style={{ margin: 0, fontSize: 18, color: '#004A98' }}>Preparing new import</h3>
              <p style={{ margin: '4px 0 0', fontSize: 14, color: '#757575' }}>Preparing the migration wizard. Previously imported data is preserved. This may take a moment.</p>
            </div>
          </div>
        )}
        <div style={{ backgroundColor: '#fff', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: 32, minHeight: 400 }}>
          {!h.selectedLocation ? (
            <LocationSetup
              locations={h.locations}
              locationIdsWithHistory={h.locationIdsWithHistory}
              onSelect={h.handleSelectLocation}
              onClearAndStartFresh={h.handleClearAndStartFresh}
            />
          ) : h.allStepsCompleted ? (
            <SummaryView
              migrationStatus={h.migrationStatus!}
              selectedLocation={h.selectedLocation!}
              onStartNewImport={h.handleStartNewImport}
              onChangeLocation={h.handleChangeLocation}
              restarting={h.restarting}
              drillDownStep={h.drillDownStep}
              setDrillDownStep={h.setDrillDownStep}
              selectedFileName={h.selectedFileName}
              fileInputRef={h.fileInputRef}
              onChooseFile={h.pickFile}
              onFileChange={h.handleFileChange}
              onRetryStep={h.handleRetryStep}
              retryingStep={h.retryingStep}
              onDownloadTemplate={h.downloadTemplate}
            />
          ) : h.isStepCompleted ? (
            <CompletionView
              step={h.activeStep}
              result={h.currentStepResult}
              index={h.currentStepIndex}
              onContinue={step => h.setActiveStep(step)}
            />
          ) : h.isStepPartial ? (
            <PartialView
              step={h.activeStep}
              result={h.currentStepResult}
              selectedFileName={h.selectedFileName}
              onChooseFile={h.pickFile}
              onFileChange={h.handleFileChange}
              onRetry={h.handleUpload}
              onDownloadTemplate={h.downloadTemplate}
              fileInputRef={h.fileInputRef}
              onSkip={h.handleSkip}
              showSkipConfirm={h.showSkipConfirm}
              setShowSkipConfirm={h.setShowSkipConfirm}
              skipping={h.skipping}
            />
          ) : h.isStepFailed ? (
            <FailedView
              step={h.activeStep}
              result={h.currentStepResult}
              selectedFileName={h.selectedFileName}
              onChooseFile={h.pickFile}
              onFileChange={h.handleFileChange}
              onRetry={h.handleUpload}
              onDownloadTemplate={h.downloadTemplate}
              fileInputRef={h.fileInputRef}
            />
          ) : h.isStepInProgress ? (
            <InProgressView step={h.activeStep} />
          ) : (
            <UploadView
              step={h.activeStep}
              selectedFileName={h.selectedFileName}
              selectedFile={h.selectedFile}
              onChooseFile={h.pickFile}
              onFileChange={h.handleFileChange}
              onUpload={h.handleUpload}
              onDownloadTemplate={h.downloadTemplate}
              fileInputRef={h.fileInputRef}
              isOptionalStep={h.isOptionalStep}
              onSkipOptional={h.handleSkipOptionalStep}
            />
          )}
        </div>
      </div>
    </div>
  );
};

/* Sub-components */

interface LocationSetupProps {
  locations: Location[];
  locationIdsWithHistory: Set<number>;
  onSelect: (loc: Location) => void;
  onClearAndStartFresh: (loc: Location) => void;
}

const LocationSetup: FC<LocationSetupProps> = ({ locations, locationIdsWithHistory, onSelect, onClearAndStartFresh }) => {
  const { createLocation, validateCodeUniqueness } = useLocationApi();
  const [mode, setMode] = useState<'select' | 'create'>('select');
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [timeZone, setTimeZone] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [confirmLocation, setConfirmLocation] = useState<Location | null>(null);
  const [clearingForFresh, setClearingForFresh] = useState(false);

  const handleSelect = useCallback((loc: Location) => {
    if (locationIdsWithHistory.has(loc.id)) {
      setConfirmLocation(loc);
    } else {
      onSelect(loc);
    }
  }, [locationIdsWithHistory, onSelect]);

  const handleConfirm = useCallback(() => {
    if (confirmLocation) {
      onSelect(confirmLocation);
      setConfirmLocation(null);
    }
  }, [confirmLocation, onSelect]);

  const handleClearFresh = useCallback(async () => {
    if (!confirmLocation) return;
    setClearingForFresh(true);
    try {
      await onClearAndStartFresh(confirmLocation);
      setConfirmLocation(null);
    } finally {
      setClearingForFresh(false);
    }
  }, [confirmLocation, onClearAndStartFresh]);

  const handleCreate = useCallback(async () => {
    if (!name.trim() || !code.trim() || !timeZone) return;
    setCreating(true);
    const location = await createLocation({
      name: name.trim(),
      code: code.trim(),
      timeZone,
      city: city.trim() || undefined,
      state: state.trim().toUpperCase() || undefined,
    });
    if (location) {
      onSelect(location);
    }
    setCreating(false);
  }, [name, code, timeZone, city, state, createLocation, onSelect]);

  if (mode === 'create') {
    return (
      <div>
        <h3 style={{ margin: '0 0 4px', fontSize: 20, fontWeight: 700, color: '#14284D' }}>Create New Location</h3>
        <p style={{ margin: '0 0 24px', fontSize: 14, color: '#757575' }}>Enter the location details to begin the migration.</p>

        <div className="card-primary">
          <div className="card-header mb-5" />
          <div className="card-body">
            <div className="grid-3 p-2" style={{ maxWidth: 900 }}>
              <TextField
                id="migration-location-name"
                name="name"
                label="NAME"
                value={name}
                onChange={setName}
                required
                allowSpaces
              />
              <TextField
                id="migration-location-code"
                name="code"
                label="CODE"
                value={code}
                onChange={setCode}
                validate={validateCodeUniqueness}
                required
              />
              <TimeZoneSelect
                timeZone={timeZone}
                setTimeZone={setTimeZone}
              />
              <TextField
                id="migration-location-city"
                name="city"
                label="CITY"
                value={city}
                onChange={setCity}
                allowSpaces
              />
              <TextField
                id="migration-location-state"
                name="state"
                label="STATE"
                value={state}
                onChange={setState}
              />
            </div>
          </div>
        </div>

        <div className="grid-2" style={{ marginTop: 24, maxWidth: 400 }}>
          <button className="btn-outline" onClick={() => setMode('select')} disabled={creating} style={{ width: '100%' }}>
            Cancel
          </button>
          <button className="btn-primary" onClick={handleCreate} disabled={creating || !name.trim() || !code.trim() || !timeZone} style={{ width: '100%' }}>
            {creating ? 'Creating...' : 'Create & Start Migration'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h3 style={{ margin: '0 0 4px', fontSize: 20, fontWeight: 700, color: '#14284D' }}>Location Setup</h3>
      <p style={{ margin: '0 0 24px', fontSize: 14, color: '#757575' }}>Select an existing location or create a new one to begin the migration.</p>

      <div style={{ marginBottom: 24 }}>
        <h4 style={{ fontSize: 14, fontWeight: 600, margin: '0 0 12px', color: '#424242' }}>Select Existing</h4>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {locations.filter(l => !l.deleted).map(loc => {
            const hasHistory = locationIdsWithHistory.has(loc.id);
            return (
              <div key={loc.id} onClick={() => handleSelect(loc)}
                style={{ padding: '12px 16px', borderRadius: 8, border: hasHistory ? '1px solid #ffe082' : '1px solid #e0e0e0', cursor: 'pointer', transition: 'all 0.15s', backgroundColor: hasHistory ? '#fffde7' : '#fff' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#004A98'; e.currentTarget.style.backgroundColor = 'rgba(0,74,152,0.04)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = hasHistory ? '#ffe082' : '#e0e0e0'; e.currentTarget.style.backgroundColor = hasHistory ? '#fffde7' : '#fff'; }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ fontWeight: 600, fontSize: 14, color: '#212121', flex: 1 }}>{loc.name}</div>
                  {hasHistory && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11, fontWeight: 600, color: '#f57f17', backgroundColor: 'rgba(245,127,23,0.1)', padding: '2px 8px', borderRadius: 10 }}>
                      <span className="material-icons" style={{ fontSize: 14 }}>history</span>
                      Imported
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: '#757575' }}>{loc.code} — {loc.city}, {loc.state} ({loc.timeZone})</div>
              </div>
            );
          })}
        </div>
      </div>

      {confirmLocation && (
        <div style={{ marginBottom: 16, padding: 16, backgroundColor: '#fff8e1', borderRadius: 10, border: '1px solid #ffe082' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <span className="material-icons" style={{ fontSize: 20, color: '#e65100' }}>info</span>
            <span style={{ fontWeight: 600, fontSize: 14, color: '#e65100' }}>Previously Migrated Location</span>
          </div>
          <p style={{ margin: '0 0 12px', fontSize: 13, color: '#616161' }}>
            <strong>{confirmLocation.name} ({confirmLocation.code})</strong> has been used in a previous migration.
            Choose how to proceed:
          </p>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn-outline" onClick={() => setConfirmLocation(null)} style={{ flex: 1, fontSize: 13 }}>
              Cancel
            </button>
            <button className="btn-outline" onClick={handleConfirm} disabled={clearingForFresh} style={{ flex: 1, fontSize: 13 }}>
              Continue with existing data
            </button>
            <button className="btn-primary" onClick={handleClearFresh} disabled={clearingForFresh} style={{ flex: 1, fontSize: 13 }}>
              {clearingForFresh ? 'Starting...' : 'Start another import'}
            </button>
          </div>
        </div>
      )}

      <button className="btn-outline" onClick={() => setMode('create')} style={{ width: '100%' }}>
        + Create New Location
      </button>
    </div>
  );
};

interface StepViewProps {
  step: MigrationStep;
  selectedFileName: string | null;
  selectedFile: File | null;
  onChooseFile: () => void;
  onFileChange: (file: File | null) => void;
  onUpload: () => void;
  onDownloadTemplate: () => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
  isOptionalStep?: boolean;
  onSkipOptional?: () => void;
}

const UploadView: FC<StepViewProps> = ({ step, selectedFileName, selectedFile, onChooseFile, onFileChange, onUpload, onDownloadTemplate, fileInputRef, isOptionalStep, onSkipOptional }) => (
  <div>
    <div style={{ marginBottom: 4 }}>
      <h3 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#14284D' }}>{STEP_LABELS[step]}</h3>
      <p style={{ margin: '4px 0 0', fontSize: 14, color: '#757575' }}>{STEP_DESCRIPTIONS[step]}</p>
    </div>
    <div onClick={onChooseFile}
      onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = '#004A98'; e.currentTarget.style.backgroundColor = 'rgba(0,74,152,0.04)'; }}
      onDragLeave={e => { e.currentTarget.style.borderColor = '#e0e0e0'; e.currentTarget.style.backgroundColor = 'transparent'; }}
      onDrop={e => { e.preventDefault(); e.currentTarget.style.borderColor = '#e0e0e0'; e.currentTarget.style.backgroundColor = 'transparent'; const f = e.dataTransfer.files[0]; if (f) onFileChange(f); }}
      style={{ border: '2px dashed #e0e0e0', borderRadius: 12, padding: 40, textAlign: 'center', cursor: 'pointer', transition: 'all 0.2s', marginTop: 16, marginBottom: 16 }}>
      <span className="material-icons" style={{ fontSize: 40, color: '#bdbdbd', marginBottom: 8 }}>cloud_upload</span>
      <p style={{ margin: '0 0 4px', fontWeight: 600, color: '#616161' }}>Drop CSV file here or click to browse</p>
      <p style={{ margin: 0, fontSize: 12, color: '#9e9e9e' }}>.csv files only</p>
    </div>
    <input ref={fileInputRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={e => onFileChange(e.target.files?.[0] || null)} />
    {selectedFileName && (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', backgroundColor: 'rgba(0,74,152,0.06)', borderRadius: 8, marginBottom: 16 }}>
        <span className="material-icons" style={{ fontSize: 20, color: '#004A98' }}>description</span>
        <span style={{ fontSize: 14, color: '#212121' }}>{selectedFileName}</span>
      </div>
    )}
    <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
      {selectedFile && <button className="btn-primary" onClick={onUpload}>Start Import</button>}
      <button className="btn-outline" onClick={onDownloadTemplate}>Download Template</button>
      {isOptionalStep && (
        <span style={{ color: '#bdbdbd', margin: '0 4px' }}>|</span>
      )}
      {isOptionalStep && (
        <button className="btn-outline" onClick={onSkipOptional} style={{ color: '#757575', borderColor: '#bdbdbd' }}>
          Skip this step
        </button>
      )}
    </div>
  </div>
);

const InProgressView: FC<{ step: MigrationStep }> = ({ step }) => (
  <div style={{ textAlign: 'center', padding: '40px 0' }}>
    <CircularProgress size={48} sx={{ color: '#004A98', marginBottom: 2 }} />
    <h3 style={{ margin: '0 0 8px', fontSize: 20, color: '#004A98' }}>Processing {STEP_LABELS[step]}...</h3>
    <p style={{ color: '#757575', fontSize: 14, margin: 0 }}>Importing records from CSV. This may take a moment.</p>
  </div>
);

interface CompletionViewProps {
  step: MigrationStep;
  result: any;
  index: number;
  onContinue: (step: MigrationStep) => void;
  onBackToSummary?: () => void;
}

const CompletionView: FC<CompletionViewProps> = ({ step, result, index, onContinue, onBackToSummary }) => {
  const isDrillDown = !!onBackToSummary;

  return (
    <div style={{ textAlign: 'center', padding: isDrillDown ? '24px 0' : '40px 0' }}>
      {onBackToSummary && (
        <div style={{ textAlign: 'left', marginBottom: 16 }}>
          <button className="btn-outline" onClick={onBackToSummary} style={{ fontSize: 13 }}>
            ← Back to Summary
          </button>
        </div>
      )}

      <span className="material-icons" style={{ fontSize: 48, color: '#2e7d32', marginBottom: 12 }}>check_circle</span>
      <h3 style={{ margin: '0 0 16px', fontSize: 22, color: '#2e7d32' }}>{STEP_LABELS[step]} — Complete</h3>

      {result && (
        <>
          <div style={{ display: 'flex', gap: 16, justifyContent: 'center', marginBottom: 16 }}>
            <div style={{ padding: '14px 24px', backgroundColor: 'rgba(46,125,50,0.06)', borderRadius: 10, border: '1px solid rgba(46,125,50,0.15)', minWidth: 140 }}>
              <div style={{ fontSize: 26, fontWeight: 700, color: '#2e7d32' }}>{(result.recordsProcessed || 0).toLocaleString()}</div>
              <div style={{ fontSize: 12, color: '#616161', marginTop: 2 }}>Records Processed</div>
            </div>
            <div style={{ padding: '14px 24px', backgroundColor: (result.recordsFailed || 0) > 0 ? 'rgba(211,47,47,0.06)' : 'rgba(158,158,158,0.06)', borderRadius: 10, border: (result.recordsFailed || 0) > 0 ? '1px solid rgba(211,47,47,0.15)' : '1px solid rgba(158,158,158,0.15)', minWidth: 140 }}>
              <div style={{ fontSize: 26, fontWeight: 700, color: (result.recordsFailed || 0) > 0 ? '#d32f2f' : '#9e9e9e' }}>{(result.recordsFailed || 0).toLocaleString()}</div>
              <div style={{ fontSize: 12, color: '#616161', marginTop: 2 }}>Records Failed</div>
            </div>
          </div>

          <div style={{ fontSize: 14, color: '#757575', marginBottom: 16 }}>
            Duration: <strong>{formatDuration(result.startedAt, result.completedAt)}</strong>
          </div>
        </>
      )}

      {!isDrillDown && index < STEP_ORDER.length - 1 && (
        <button className="btn-primary" onClick={() => onContinue(STEP_ORDER[index + 1])} style={{ marginTop: 16 }}>
          Continue to {STEP_LABELS[STEP_ORDER[index + 1]]}
        </button>
      )}
      {!isDrillDown && index === STEP_ORDER.length - 1 && (
        <div style={{ marginTop: 16 }}>
          <p style={{ color: '#004A98', fontWeight: 600, fontSize: 16 }}>All migration steps completed!</p>
        </div>
      )}
    </div>
  );
};

interface PartialViewProps {
  step: MigrationStep;
  result: any;
  selectedFileName: string | null;
  onChooseFile: () => void;
  onFileChange: (file: File | null) => void;
  onRetry: () => void;
  onDownloadTemplate: () => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
  onSkip: () => void;
  showSkipConfirm: boolean;
  setShowSkipConfirm: (v: boolean) => void;
  skipping: boolean;
}

const PartialView: FC<PartialViewProps> = ({ step, result, selectedFileName, onChooseFile, onFileChange, onRetry, onDownloadTemplate, fileInputRef, onSkip, showSkipConfirm, setShowSkipConfirm, skipping }) => (
  <div>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
      <span className="material-icons" style={{ color: '#f57f17' }}>warning</span>
      <h3 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#f57f17' }}>{STEP_LABELS[step]} — Completed with Errors</h3>
    </div>
    <p style={{ color: '#757575', fontSize: 14, margin: '0 0 16px' }}>
      {result?.recordsProcessed || 0} records processed, {result?.recordsFailed || 0} failed
    </p>

    {result?.errors && <ErrorList errors={result.errors} />}

    <div style={{ marginTop: 24, borderTop: '1px solid #e0e0e0', paddingTop: 16 }}>
      <p style={{ margin: '0 0 12px', fontSize: 13, color: '#616161' }}>
        Choose a corrected CSV to retry the failed records, or skip to continue with the next step.
      </p>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <input ref={fileInputRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={e => onFileChange(e.target.files?.[0] || null)} />
        {selectedFileName && <span style={{ fontSize: 13, color: '#616161' }}>{selectedFileName}</span>}
        <button className="btn-outline" onClick={onChooseFile}>Upload Corrected File</button>
        {selectedFileName && <button className="btn-primary" onClick={onRetry}>Retry Upload</button>}
        <button className="btn-outline" onClick={onDownloadTemplate}>Download Template</button>

        <span style={{ color: '#bdbdbd', margin: '0 4px' }}>|</span>

        {!showSkipConfirm ? (
          <button className="btn-outline" onClick={() => setShowSkipConfirm(true)} disabled={skipping} style={{ color: '#f57f17', borderColor: '#f57f17' }}>
            {skipping ? 'Skipping...' : 'Skip & Continue'}
          </button>
        ) : (
          <div style={{ marginLeft: 8, padding: 8, backgroundColor: '#fff8e1', borderRadius: 6, border: '1px solid #ffe082', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12, color: '#e65100', maxWidth: 280 }}>
              This will skip the failed records. They will not be available in later steps.
            </span>
            <button className="btn-outline" onClick={() => setShowSkipConfirm(false)} disabled={skipping} style={{ fontSize: 12 }}>Cancel</button>
            <button className="btn-danger" onClick={onSkip} disabled={skipping} style={{ fontSize: 12 }}>
              {skipping ? 'Skipping...' : 'Proceed'}
            </button>
          </div>
        )}
      </div>
    </div>
  </div>
);

function formatDuration(start?: string | null, end?: string | null): string {
  if (!start || !end) return '-';
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (ms <= 0) return '-';
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  const rem = sec % 60;
  return `${min}m ${rem}s`;
}

interface SummaryViewProps {
  migrationStatus: MigrationStatusResponse;
  selectedLocation: Location;
  onStartNewImport: () => void;
  onChangeLocation: () => void;
  restarting: boolean;
  drillDownStep: MigrationStep | null;
  setDrillDownStep: (step: MigrationStep | null) => void;
  selectedFileName: string | null;
  fileInputRef: React.RefObject<HTMLInputElement>;
  onChooseFile: () => void;
  onFileChange: (file: File | null) => void;
  onRetryStep: (step: MigrationStep) => void;
  retryingStep: MigrationStep | null;
  onDownloadTemplate: (step: MigrationStep) => void;
}

const SummaryView: FC<SummaryViewProps> = ({ migrationStatus, selectedLocation, onStartNewImport, onChangeLocation, restarting, drillDownStep, setDrillDownStep, selectedFileName, fileInputRef, onChooseFile, onFileChange, onRetryStep, retryingStep, onDownloadTemplate }) => {
  const { steps, startedAt, completedAt } = migrationStatus;

  // Drill-down mode: show a single step's detail view
  if (drillDownStep) {
    const stepResult = steps?.find(s => s.step === drillDownStep);
    const stepIndex = STEP_ORDER.indexOf(drillDownStep);
    if (stepResult) {
      const backButton = (
        <div style={{ textAlign: 'left', marginBottom: 16 }}>
          <button className="btn-outline" onClick={() => setDrillDownStep(null)} style={{ fontSize: 13 }}>
            ← Back to Summary
          </button>
        </div>
      );

      if (stepResult.status === 'COMPLETED') {
        const isLastStep = stepIndex === STEP_ORDER.length - 1;
        return (
          <div style={{ padding: '24px 0' }}>
            {backButton}
            <div style={{ textAlign: 'center' }}>
              <span className="material-icons" style={{ fontSize: 48, color: '#2e7d32', marginBottom: 12 }}>check_circle</span>
              <h3 style={{ margin: '0 0 16px', fontSize: 22, color: '#2e7d32' }}>{STEP_LABELS[drillDownStep]} — Complete</h3>
              <div style={{ display: 'flex', gap: 16, justifyContent: 'center', marginBottom: 16 }}>
                <div style={{ padding: '14px 24px', backgroundColor: 'rgba(46,125,50,0.06)', borderRadius: 10, border: '1px solid rgba(46,125,50,0.15)', minWidth: 140 }}>
                  <div style={{ fontSize: 26, fontWeight: 700, color: '#2e7d32' }}>{(stepResult.recordsProcessed || 0).toLocaleString()}</div>
                  <div style={{ fontSize: 12, color: '#616161', marginTop: 2 }}>Records Processed</div>
                </div>
                <div style={{ padding: '14px 24px', backgroundColor: (stepResult.recordsFailed || 0) > 0 ? 'rgba(211,47,47,0.06)' : 'rgba(158,158,158,0.06)', borderRadius: 10, border: (stepResult.recordsFailed || 0) > 0 ? '1px solid rgba(211,47,47,0.15)' : '1px solid rgba(158,158,158,0.15)', minWidth: 140 }}>
                  <div style={{ fontSize: 26, fontWeight: 700, color: (stepResult.recordsFailed || 0) > 0 ? '#d32f2f' : '#9e9e9e' }}>{(stepResult.recordsFailed || 0).toLocaleString()}</div>
                  <div style={{ fontSize: 12, color: '#616161', marginTop: 2 }}>Records Failed</div>
                </div>
              </div>
              <div style={{ fontSize: 14, color: '#757575', marginBottom: 16 }}>
                Duration: <strong>{formatDuration(stepResult.startedAt, stepResult.completedAt)}</strong>
              </div>
              {!isLastStep && (
                <button className="btn-primary" onClick={() => setDrillDownStep(STEP_ORDER[stepIndex + 1])} style={{ marginTop: 8 }}>
                  Continue to {STEP_LABELS[STEP_ORDER[stepIndex + 1]]}
                </button>
              )}
              {isLastStep && (
                <p style={{ color: '#004A98', fontWeight: 600, fontSize: 16, marginTop: 16 }}>All migration steps completed!</p>
              )}
            </div>
          </div>
        );
      }

      if (stepResult.status === 'PARTIALLY_COMPLETED') {
        const isRetrying = retryingStep === drillDownStep;
        return (
          <div style={{ padding: '24px 0' }}>
            {backButton}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <span className="material-icons" style={{ color: '#f57f17' }}>warning</span>
              <h3 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#f57f17' }}>{STEP_LABELS[drillDownStep]} — Completed with Errors</h3>
            </div>
            <p style={{ color: '#757575', fontSize: 14, margin: '0 0 16px' }}>
              {stepResult.recordsProcessed || 0} records processed, {stepResult.recordsFailed || 0} failed
            </p>
            {stepResult.errors && stepResult.errors.length > 0 && <ErrorList errors={stepResult.errors} />}

            {/* Retry upload controls */}
            <div style={{ marginTop: 24, borderTop: '1px solid #e0e0e0', paddingTop: 16 }}>
              <p style={{ margin: '0 0 12px', fontSize: 13, color: '#616161' }}>
                Choose a corrected CSV to retry the failed records, or go back to the summary.
              </p>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                <input ref={fileInputRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={e => onFileChange(e.target.files?.[0] || null)} />
                {selectedFileName && <span style={{ fontSize: 13, color: '#616161' }}>{selectedFileName}</span>}
                <button className="btn-outline" onClick={onChooseFile} style={{ fontSize: 13 }}>Upload Corrected File</button>
                {selectedFileName && (
                  <button className="btn-primary" onClick={() => onRetryStep(drillDownStep)} disabled={isRetrying} style={{ fontSize: 13 }}>
                    {isRetrying ? 'Uploading...' : 'Retry Upload'}
                  </button>
                )}
                <button className="btn-outline" onClick={() => onDownloadTemplate(drillDownStep)} style={{ fontSize: 13 }}>Download Template</button>
              </div>
            </div>
          </div>
        );
      }
    }
  }

  // Summary table mode
  const anyPartial = steps?.some(s => s.status === 'PARTIALLY_COMPLETED');
  const allSuccess = !anyPartial;
  const color = allSuccess ? '#2e7d32' : '#f57f17';
  const totalProcessed = steps?.reduce((sum, s) => sum + (s.recordsProcessed || 0), 0) || 0;
  const totalFailed = steps?.reduce((sum, s) => sum + (s.recordsFailed || 0), 0) || 0;

  return (
    <div style={{ padding: '24px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
        <span className="material-icons" style={{ fontSize: 40, color }}>{allSuccess ? 'check_circle' : 'warning'}</span>
        <h2 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#14284D' }}>Migration Complete</h2>
      </div>

      <div style={{ display: 'flex', gap: 24, marginBottom: 24, flexWrap: 'wrap', fontSize: 14, color: '#616161' }}>
        <div><strong>Location:</strong> {selectedLocation.name} ({selectedLocation.code})</div>
        <div><strong>Duration:</strong> {formatDuration(startedAt, completedAt)}</div>
        <div><strong>Status:</strong> <span style={{ color }}>{allSuccess ? 'All steps completed successfully' : 'Completed with some errors'}</span></div>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
        <div style={{ flex: 1, padding: '16px 20px', backgroundColor: 'rgba(46,125,50,0.06)', borderRadius: 10, border: '1px solid rgba(46,125,50,0.15)' }}>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#2e7d32' }}>{totalProcessed.toLocaleString()}</div>
          <div style={{ fontSize: 12, color: '#616161', marginTop: 2 }}>Records Processed</div>
        </div>
        <div style={{ flex: 1, padding: '16px 20px', backgroundColor: totalFailed > 0 ? 'rgba(211,47,47,0.06)' : 'rgba(158,158,158,0.06)', borderRadius: 10, border: totalFailed > 0 ? '1px solid rgba(211,47,47,0.15)' : '1px solid rgba(158,158,158,0.15)' }}>
          <div style={{ fontSize: 28, fontWeight: 700, color: totalFailed > 0 ? '#d32f2f' : '#9e9e9e' }}>{totalFailed.toLocaleString()}</div>
          <div style={{ fontSize: 12, color: '#616161', marginTop: 2 }}>Records Failed</div>
        </div>
        <div style={{ flex: 1, padding: '16px 20px', backgroundColor: 'rgba(0,74,152,0.06)', borderRadius: 10, border: '1px solid rgba(0,74,152,0.15)' }}>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#004A98' }}>{steps?.length || 0}</div>
          <div style={{ fontSize: 12, color: '#616161', marginTop: 2 }}>Steps Completed</div>
        </div>
      </div>

      {/* Step breakdown table */}
      <div style={{ borderRadius: 10, border: '1px solid #e0e0e0', overflow: 'hidden', marginBottom: 24 }}>
        <div style={{ display: 'flex', padding: '12px 16px', backgroundColor: '#f5f5f5', fontWeight: 600, fontSize: 12, color: '#616161', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #e0e0e0' }}>
          <div style={{ flex: 2 }}>Step</div>
          <div style={{ flex: 1, textAlign: 'right' }}>Records</div>
          <div style={{ flex: 1, textAlign: 'right' }}>Failed</div>
          <div style={{ flex: 1, textAlign: 'center' }}>Status</div>
          <div style={{ flex: 1, textAlign: 'right' }}>Time</div>
        </div>
        {steps?.map((s, i) => {
          const skipped = s.status === 'PENDING' && OPTIONAL_STEPS.includes(s.step);
          const stColor = s.status === 'COMPLETED' ? '#2e7d32' : s.status === 'PARTIALLY_COMPLETED' ? '#f57f17' : skipped ? '#9e9e9e' : '#d32f2f';
          const stIcon = s.status === 'COMPLETED' ? 'check_circle' : s.status === 'PARTIALLY_COMPLETED' ? 'warning' : skipped ? 'radio_button_unchecked' : 'error';
          return (
            <div key={i} onClick={() => !skipped && setDrillDownStep(s.step)} style={{ display: 'flex', padding: '10px 16px', alignItems: 'center', fontSize: 13, cursor: skipped ? 'default' : 'pointer', borderBottom: i < (steps?.length || 0) - 1 ? '1px solid #f0f0f0' : 'none' }}
              onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#f5f5f5'; }}
              onMouseLeave={e => { e.currentTarget.style.backgroundColor = ''; }}>
              <div style={{ flex: 2, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className="material-icons" style={{ fontSize: 18, color: stColor }}>{stIcon}</span>
                {STEP_LABELS[s.step]}
              </div>
              <div style={{ flex: 1, textAlign: 'right', color: '#212121', fontWeight: 500 }}>{s.recordsProcessed?.toLocaleString() || 0}</div>
              <div style={{ flex: 1, textAlign: 'right', color: (s.recordsFailed || 0) > 0 ? '#d32f2f' : '#9e9e9e', fontWeight: 500 }}>{s.recordsFailed?.toLocaleString() || 0}</div>
              <div style={{ flex: 1, textAlign: 'center' }}>
                <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 10, fontSize: 11, fontWeight: 600, color: stColor, backgroundColor: `${stColor}14`, textTransform: 'uppercase' }}>
                  {s.status === 'COMPLETED' ? 'Done' : s.status === 'PARTIALLY_COMPLETED' ? 'Partial' : skipped ? 'Skipped' : 'Failed'}
                </span>
              </div>
              <div style={{ flex: 1, textAlign: 'right', color: '#757575' }}>{formatDuration(s.startedAt, s.completedAt)}</div>
            </div>
          );
        })}
      </div>

      {/* Success message */}
      <div style={{ padding: 16, backgroundColor: 'rgba(46,125,50,0.06)', borderRadius: 10, border: '1px solid rgba(46,125,50,0.15)', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <span className="material-icons" style={{ fontSize: 20, color: '#2e7d32' }}>check_circle</span>
          <span style={{ fontWeight: 600, fontSize: 14, color: '#2e7d32' }}>Migration complete</span>
        </div>
        <p style={{ margin: 0, fontSize: 13, color: '#616161' }}>
          All steps have been processed for <strong>{selectedLocation.name} ({selectedLocation.code})</strong>.
          The imported data is now available in the system.
        </p>
      </div>

      {/* Actions */}
      <div style={{ borderTop: '1px solid #e0e0e0', paddingTop: 20, display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
        <button className="btn-outline" onClick={onChangeLocation} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 24px', fontSize: 14 }}>
          <span className="material-icons" style={{ fontSize: 20 }}>add_location</span>
          Import Another Location
        </button>
        <button className="btn-primary" onClick={() => { if (!restarting) { setDrillDownStep(null); onStartNewImport(); } }} disabled={restarting} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 24px', fontSize: 14 }}>
          <span className="material-icons" style={{ fontSize: 20 }}>refresh</span>
          {restarting ? 'Starting...' : 'Start Another Import'}
        </button>
      </div>
      {restarting && (
        <p style={{ textAlign: 'center', fontSize: 13, color: '#757575', marginTop: 12 }}>
          Preparing a new import. Previously imported data is preserved...
        </p>
      )}
    </div>
  );
};

interface FailedViewProps {
  step: MigrationStep;
  result: any;
  selectedFileName: string | null;
  onChooseFile: () => void;
  onFileChange: (file: File | null) => void;
  onRetry: () => void;
  onDownloadTemplate: () => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
}

const FailedView: FC<FailedViewProps> = ({ step, result, selectedFileName, onChooseFile, onFileChange, onRetry, onDownloadTemplate, fileInputRef }) => (
  <div>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
      <span className="material-icons" style={{ color: '#d32f2f' }}>error</span>
      <h3 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#d32f2f' }}>{STEP_LABELS[step]} — Failed</h3>
    </div>
    <p style={{ color: '#757575', fontSize: 14, margin: '0 0 16px' }}>
      {result?.recordsProcessed || 0} records processed, {result?.recordsFailed || 0} failed
    </p>
    {result?.errors && <ErrorList errors={result.errors} />}

    <div style={{ marginTop: 24, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
      <input ref={fileInputRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={e => onFileChange(e.target.files?.[0] || null)} />
      {selectedFileName && <span style={{ fontSize: 13, color: '#616161' }}>{selectedFileName}</span>}
      <button className="btn-outline" onClick={onChooseFile}>Choose Corrected File</button>
      {selectedFileName && <button className="btn-primary" onClick={onRetry}>Retry Upload</button>}
      <button className="btn-outline" onClick={onDownloadTemplate}>Download Template</button>
    </div>
  </div>
);

export default MigrationPage;
